import React, { useState, useEffect, useRef } from 'react';
import ClassParserService from '../../services/ClassParserService';
import TeacherAssignmentsService from '../../services/TeacherAssignmentsService';
import { classFilesData } from '../../Data/classFilesData';
import './SingleClassParser.css';

const SingleClassParser = ({ tenantId, onClose, onAssignmentsUpdated }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [previewAssignments, setPreviewAssignments] = useState([]);
  const [approvedAssignments, setApprovedAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  
  const fileInputRef = useRef(null);
  const availableClasses = Object.keys(classFilesData).sort();

  // Fetch available teachers
  const fetchTeachers = async () => {
    if (!tenantId) {
      setError('Tenant ID is required to load teachers');
      return;
    }

    try {
      setLoadingTeachers(true);
      setError('');
      console.log('Fetching teachers for tenantId:', tenantId);
      
      const teachers = await TeacherAssignmentsService.getAllTeachers(tenantId);
      console.log('Fetched teachers:', teachers);
      
      setAvailableTeachers(teachers);
      setMessage(`Loaded ${teachers.length} teachers successfully`);
      setMessageType('success');
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setError(`Failed to load teachers list: ${error.message}`);
      setAvailableTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Load teachers on component mount
  useEffect(() => {
    if (tenantId) {
      fetchTeachers();
    }
  }, [tenantId]);

  // File upload handlers
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      console.log('Starting file upload process for:', file.name);
      const content = await readFileContent(file);
      const fileName = file.name.replace('.txt', '');
      
      console.log('File content loaded, length:', content.length);
      console.log('File name:', fileName);
      console.log('Tenant ID:', tenantId);
      
      // Parse uploaded file
      console.log('Starting parsing with ClassParserService...');
      const assignments = await ClassParserService.parseClassFile(content, fileName, tenantId);
      console.log('Parsing completed, assignments found:', assignments.length);
      console.log('Assignments data:', assignments);
      
      // Add selection state to each assignment
      const assignmentsWithSelection = assignments.map(assignment => ({
        ...assignment,
        isSelected: false,
        isNew: true
      }));
      
      console.log('Assignments with selection state:', assignmentsWithSelection);
      setPreviewAssignments(assignmentsWithSelection);
      setSelectedClass(fileName);
      setUploadedFile(file);
      
      if (assignmentsWithSelection.length > 0) {
        setMessage(`Successfully parsed ${assignmentsWithSelection.length} assignments from ${fileName}`);
        setMessageType('success');
      } else {
        setMessage(`No assignments found in ${fileName}. Please check the file format.`);
        setMessageType('warning');
      }
      
    } catch (err) {
      console.error('Error in handleFileUpload:', err);
      setError(`Failed to parse uploaded file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file, 'UTF-8');
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const txtFile = files.find(file => file.name.endsWith('.txt'));
    
    if (txtFile) {
      handleFileUpload(txtFile);
    } else {
      setError('Please upload a .txt file');
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Edit functionality
  const handleEditAssignment = (assignment, index) => {
    setEditingAssignment({ ...assignment, index });
    setEditFormData({
      assignment_type: assignment.assignment_type,
      teacher_id: assignment.teacher_id || '',
      teacher_name: assignment.assignment_data?.teacher_name || '',
      subject_name: assignment.subject_name || '',
      responsibilities: assignment.responsibilities?.join(', ') || '',
      duty_location: assignment.duty_location || '',
      teaching_level: assignment.teaching_level || '',
      is_primary: assignment.is_primary || false,
      member_role: assignment.member_role || ''
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    if (!editingAssignment) return;

    const updatedAssignments = [...previewAssignments];
    const index = editingAssignment.index;
    
    // Find the selected teacher
    const selectedTeacher = availableTeachers.find(teacher => teacher.id === editFormData.teacher_id);
    
    updatedAssignments[index] = {
      ...updatedAssignments[index],
      assignment_type: editFormData.assignment_type,
      teacher_id: editFormData.teacher_id,
      subject_name: editFormData.subject_name,
      responsibilities: editFormData.responsibilities.split(',').map(r => r.trim()).filter(r => r),
      duty_location: editFormData.duty_location,
      teaching_level: editFormData.teaching_level,
      is_primary: editFormData.is_primary,
      member_role: editFormData.member_role,
      assignment_data: {
        ...updatedAssignments[index].assignment_data,
        teacher_id: editFormData.teacher_id,
        teacher_name: selectedTeacher ? selectedTeacher.name : editFormData.teacher_name,
        teacher_code: selectedTeacher ? selectedTeacher.code : updatedAssignments[index].assignment_data?.teacher_code
      }
    };

    setPreviewAssignments(updatedAssignments);
    setEditingAssignment(null);
    setEditFormData({});
    
    // Update approved assignments if this one was selected
    const approved = updatedAssignments.filter(assignment => assignment.isSelected);
    setApprovedAssignments(approved);
  };

  const handleCancelEdit = () => {
    setEditingAssignment(null);
    setEditFormData({});
  };

  const handleClassSelect = async (className) => {
    if (!className) return;
    
    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      // Parse single class file
      const assignments = await ClassParserService.parseClassFile(
        classFilesData[className], 
        className, 
        tenantId
      );
      
      // Add selection state to each assignment
      const assignmentsWithSelection = assignments.map(assignment => ({
        ...assignment,
        isSelected: false,
        isNew: true
      }));
      
      setPreviewAssignments(assignmentsWithSelection);
      setSelectedClass(className);
      
    } catch (err) {
      setError(`Failed to parse class file: ${err.message}`);
      console.error('Error parsing class file:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentToggle = (index) => {
    const updatedAssignments = [...previewAssignments];
    updatedAssignments[index].isSelected = !updatedAssignments[index].isSelected;
    setPreviewAssignments(updatedAssignments);
    
    // Update approved assignments
    const approved = updatedAssignments.filter(assignment => assignment.isSelected);
    setApprovedAssignments(approved);
  };

  const handleSelectAll = () => {
    const allSelected = previewAssignments.every(assignment => assignment.isSelected);
    const updatedAssignments = previewAssignments.map(assignment => ({
      ...assignment,
      isSelected: !allSelected
    }));
    setPreviewAssignments(updatedAssignments);
    
    const approved = updatedAssignments.filter(assignment => assignment.isSelected);
    setApprovedAssignments(approved);
  };

  const handleSaveApproved = async () => {
    if (approvedAssignments.length === 0) {
      setError('Please select at least one assignment to save');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Filter out duplicates and remove selection state before saving
      const assignmentsToSave = approvedAssignments
        .filter(assignment => !assignment.isDuplicate)
        .map(({ isSelected, isNew, isDuplicate, ...assignment }) => assignment);
      
      if (assignmentsToSave.length === 0) {
        setMessage('All selected assignments are duplicates and will not be saved');
        setMessageType('info');
        return;
      }
      
      // Save to database
      const createdAssignments = await TeacherAssignmentsService.bulkInsertAssignments(assignmentsToSave);
      
      const duplicateCount = approvedAssignments.filter(a => a.isDuplicate).length;
      let message = `Successfully saved ${createdAssignments.length} assignments for ${selectedClass}`;
      if (duplicateCount > 0) {
        message += ` (${duplicateCount} duplicates skipped)`;
      }
      
      setMessage(message);
      setMessageType('success');
      
      // Notify parent component
      if (onAssignmentsUpdated) {
        onAssignmentsUpdated();
      }
      
      // Clear preview
      setPreviewAssignments([]);
      setApprovedAssignments([]);
      setSelectedClass('');
      
    } catch (err) {
      setError(`Failed to save assignments: ${err.message}`);
      console.error('Error saving assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAssignmentTypeLabel = (type) => {
    switch (type) {
      case 'class_master': return 'Class Master';
      case 'vice_class_master': return 'Vice Class Master';
      case 'subject_teacher': return 'Subject Teacher';
      default: return type;
    }
  };

  return (
    <div className="single-class-parser-modal">
      <div className="single-class-parser-container">
        {/* Header */}
        <div className="parser-header">
          <h2>
            <span>📚</span>
            Single Class Parser
          </h2>
          <p>Parse one class file at a time and review assignments before saving</p>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Content */}
        <div className="parser-content">
          {/* File Upload Section */}
          <div className="file-upload-section">
            <h4>
              <span>📁</span>
              Upload Class File
            </h4>
            <div
              className={`file-upload-area ${isDragOver ? 'dragover' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="file-upload-icon">📄</div>
              <div className="file-upload-text">
                {uploadedFile ? `Uploaded: ${uploadedFile.name}` : 'Drop your class file here or click to browse'}
              </div>
              <div className="file-upload-hint">
                Supported format: .txt files
              </div>
              <button className="upload-btn">
                Choose File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt"
                onChange={handleFileInputChange}
                className="file-input"
              />
            </div>
            <div className="file-format-info">
              <h5>Expected File Format:</h5>
              <p>
                <strong>Class 1A.txt</strong> - Contains class information with:<br/>
                • 班主任: [Teacher Code]<br/>
                • 副班主任: [Teacher Code]<br/>
                • 課室: [Room Number]<br/>
                • Subject: Teacher Code (e.g., 中文: 郭豫婷)
              </p>
            </div>
          </div>

          {/* Class Selection */}
          <div className="class-selection">
            <label>Select from Pre-loaded Classes:</label>
            <select
              value={selectedClass}
              onChange={(e) => handleClassSelect(e.target.value)}
              className="class-select"
              disabled={loading}
            >
              <option value="">Choose a pre-loaded class...</option>
              {availableClasses.map(className => (
                <option key={className} value={className}>
                  {className}
                </option>
              ))}
            </select>
          </div>

          {/* Messages */}
          {error && (
            <div className="message error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className={`message ${messageType}`}>
              <span>{messageType === 'success' ? '✅' : messageType === 'info' ? 'ℹ️' : '⚠️'}</span>
              <span>{message}</span>
            </div>
          )}

          {/* Preview Assignments */}
          {previewAssignments.length > 0 && (
            <div>
              {/* Controls */}
              <div className="parser-controls">
                <div className="control-left">
                  <button
                    onClick={handleSelectAll}
                    className="select-all-btn"
                  >
                    {previewAssignments.every(a => a.isSelected) ? 'Deselect All' : 'Select All'}
                  </button>
                  <div className="selection-counter">
                    {approvedAssignments.length} of {previewAssignments.length} selected
                  </div>
                </div>
                <button
                  onClick={handleSaveApproved}
                  disabled={approvedAssignments.length === 0 || loading}
                  className="save-btn"
                >
                  {loading ? 'Saving...' : `Save ${approvedAssignments.length} Selected`}
                </button>
              </div>

              {/* Assignment List */}
              <div className="assignments-list">
                {previewAssignments.map((assignment, index) => (
                  <div
                    key={index}
                    className={`assignment-item ${
                      assignment.isSelected ? 'selected' : ''
                    } ${assignment.isDuplicate ? 'duplicate' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={assignment.isSelected}
                      onChange={() => handleAssignmentToggle(index)}
                      className="assignment-checkbox"
                    />
                    <div className="assignment-content">
                      <div className="assignment-header">
                        <span className={`assignment-type-badge ${assignment.assignment_type}`}>
                          {getAssignmentTypeLabel(assignment.assignment_type)}
                        </span>
                        {assignment.is_primary && (
                          <span className="assignment-badge class-master-teacher">Class Master Teacher</span>
                        )}
                        {assignment.isDuplicate && (
                          <span className="assignment-badge duplicate">Duplicate</span>
                        )}
                        {assignment.assignment_data?.needs_update && (
                          <span className="assignment-badge needs-update">Needs Update</span>
                        )}
                      </div>
                      
                      <div className="assignment-details">
                        <div className="assignment-detail">
                          <div className="assignment-detail-label">Teacher</div>
                          <div className="assignment-detail-value">
                            {assignment.assignment_data?.teacher_name || 'Unknown'}
                          </div>
                        </div>
                        <div className="assignment-detail">
                          <div className="assignment-detail-label">Class</div>
                          <div className="assignment-detail-value">{assignment.class_id}</div>
                        </div>
                        {assignment.subject_name && (
                          <div className="assignment-detail">
                            <div className="assignment-detail-label">Subject</div>
                            <div className="assignment-detail-value">{assignment.subject_name}</div>
                          </div>
                        )}
                        <div className="assignment-detail">
                          <div className="assignment-detail-label">Responsibilities</div>
                          <div className="assignment-detail-value">
                            {assignment.responsibilities?.join(', ')}
                          </div>
                        </div>
                        <div className="assignment-detail">
                          <div className="assignment-detail-label">Classroom</div>
                          <div className="assignment-detail-value">
                            {assignment.duty_location || 'Not specified'}
                          </div>
                        </div>
                        <div className="assignment-detail">
                          <div className="assignment-detail-label">Teaching Level</div>
                          <div className="assignment-detail-value">{assignment.teaching_level}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditAssignment(assignment, index)}
                        className="edit-assignment-btn"
                        title="Edit Assignment"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <span>Processing...</span>
            </div>
          )}

          {/* Edit Assignment Modal */}
          {editingAssignment && (
            <div className="edit-modal-overlay">
              <div className="edit-modal">
                <div className="edit-modal-header">
                  <h3>Edit Assignment</h3>
                  <button onClick={handleCancelEdit} className="close-edit-btn">×</button>
                </div>
                <div className="edit-modal-content">
                  <div className="edit-form">
                    <div className="form-group">
                      <label>Teacher Name:</label>
                      {loadingTeachers ? (
                        <div className="loading-teachers">
                          <span>Loading teachers...</span>
                        </div>
                      ) : availableTeachers.length > 0 ? (
                        <select
                          value={editFormData.teacher_id}
                          onChange={(e) => {
                            const selectedTeacher = availableTeachers.find(teacher => teacher.id === e.target.value);
                            handleEditFormChange('teacher_id', e.target.value);
                            handleEditFormChange('teacher_name', selectedTeacher ? selectedTeacher.name : '');
                          }}
                          className="form-input"
                        >
                          <option value="">Select a teacher...</option>
                          {availableTeachers.map(teacher => (
                            <option key={teacher.id} value={teacher.id}>
                              {teacher.name} {teacher.english_name ? `(${teacher.english_name})` : ''} - {teacher.department || 'No Department'}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="no-teachers">
                          <span>No teachers available</span>
                          <button 
                            onClick={fetchTeachers} 
                            className="retry-btn"
                            disabled={loadingTeachers}
                          >
                            Retry
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Assignment Type:</label>
                      <select
                        value={editFormData.assignment_type}
                        onChange={(e) => handleEditFormChange('assignment_type', e.target.value)}
                        className="form-input"
                      >
                        <option value="class_master">Class Master</option>
                        <option value="vice_class_master">Vice Class Master</option>
                        <option value="subject_teacher">Subject Teacher</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Subject Name:</label>
                      <input
                        type="text"
                        value={editFormData.subject_name}
                        onChange={(e) => handleEditFormChange('subject_name', e.target.value)}
                        className="form-input"
                        placeholder="Enter subject name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Responsibilities:</label>
                      <input
                        type="text"
                        value={editFormData.responsibilities}
                        onChange={(e) => handleEditFormChange('responsibilities', e.target.value)}
                        className="form-input"
                        placeholder="Enter responsibilities (comma-separated)"
                      />
                    </div>

                    <div className="form-group">
                      <label>Classroom:</label>
                      <input
                        type="text"
                        value={editFormData.duty_location}
                        onChange={(e) => handleEditFormChange('duty_location', e.target.value)}
                        className="form-input"
                        placeholder="Enter classroom"
                      />
                    </div>

                    <div className="form-group">
                      <label>Teaching Level:</label>
                      <select
                        value={editFormData.teaching_level}
                        onChange={(e) => handleEditFormChange('teaching_level', e.target.value)}
                        className="form-input"
                      >
                        <option value="Junior">Junior</option>
                        <option value="Senior">Senior</option>
                        <option value="Unknown">Unknown</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={editFormData.is_primary}
                          onChange={(e) => handleEditFormChange('is_primary', e.target.checked)}
                        />
                        Class Master Teacher
                      </label>
                    </div>

                    <div className="form-group">
                      <label>Member Role:</label>
                      <input
                        type="text"
                        value={editFormData.member_role}
                        onChange={(e) => handleEditFormChange('member_role', e.target.value)}
                        className="form-input"
                        placeholder="Enter member role"
                      />
                    </div>
                  </div>
                </div>
                <div className="edit-modal-footer">
                  <button onClick={handleCancelEdit} className="cancel-edit-btn">
                    Cancel
                  </button>
                  <button onClick={handleSaveEdit} className="save-edit-btn">
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleClassParser;
